import { Injectable, NgZone, signal } from '@angular/core';
import { Router } from '@angular/router';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { Subject } from 'rxjs';
import { Message } from '../../models/message.model';
import { Status } from '../../models/status.model';
import { SseEventEnvelope } from '../../models/sse-event-envelope.model';
import {
  ConversationDerivacionPopupSseEvent,
  ConversationDerivacionSseEvent,
  ConversationStateSseEvent,
  ExternalChatViewers,
  SidebarSseEvent,
  SenderUnreadSseEvent,
} from '../../models/sse-chatlink-events.model';
import {
  InternalChatMessage,
  InternalMessageStatus,
  InternalUserPresence,
} from '../../models/internal-chat.model';
import { environment } from '../../../environments/environment';
import { NotificationService } from '../others/toast-service.service';
import { AuthSessionService } from '../others/auth-session.service';

@Injectable({
  providedIn: 'root',
})
export class SseService {
  private readonly reconnectDelayMs = 3000;
  private readonly maxReconnectDelayMs = 30000;
  private readonly defaultHeartbeatIntervalMs = 30000;
  private readonly heartbeatTimeoutFactor = 2.5;
  private readonly connectionStatusState = signal<
    'connected' | 'connecting' | 'disconnected'
  >('disconnected');
  readonly connectionStatus = this.connectionStatusState.asReadonly();
  private readonly sseHealthState = signal<{
    healthy: boolean;
    connectionId: string | null;
    lastHeartbeatAt: number | null;
    heartbeatIntervalMs: number;
    reconnectTimeMs: number;
    jwtExpiresAt: string | null;
    reason: string | null;
  }>({
    healthy: false,
    connectionId: null,
    lastHeartbeatAt: null,
    heartbeatIntervalMs: this.defaultHeartbeatIntervalMs,
    reconnectTimeMs: this.reconnectDelayMs,
    jwtExpiresAt: null,
    reason: null,
  });
  readonly sseHealth = this.sseHealthState.asReadonly();

  public messageReceived: Subject<Message> = new Subject<Message>();
  public myMessageReceived: Subject<Message> = new Subject<Message>();
  public statusReceived: Subject<Status> = new Subject<Status>();
  public internalMessageReceived: Subject<InternalChatMessage> =
    new Subject<InternalChatMessage>();
  public internalMessageStatusReceived: Subject<InternalMessageStatus> =
    new Subject<InternalMessageStatus>();
  public internalUserPresenceReceived: Subject<InternalUserPresence> =
    new Subject<InternalUserPresence>();
  public internalUsersPresenceSnapshotReceived: Subject<InternalUserPresence[]> =
    new Subject<InternalUserPresence[]>();
  public externalChatViewersReceived: Subject<ExternalChatViewers> =
    new Subject<ExternalChatViewers>();
  public conversationDerivacionReceived: Subject<ConversationDerivacionSseEvent> =
    new Subject<ConversationDerivacionSseEvent>();
  public conversationDerivacionPopupReceived: Subject<ConversationDerivacionPopupSseEvent> =
    new Subject<ConversationDerivacionPopupSseEvent>();
  public sidebarChanged: Subject<SidebarSseEvent> =
    new Subject<SidebarSseEvent>();
  public conversationStateChanged: Subject<ConversationStateSseEvent> =
    new Subject<ConversationStateSseEvent>();
  public senderUnreadChanged: Subject<SenderUnreadSseEvent> =
    new Subject<SenderUnreadSseEvent>();
  public reconnected: Subject<void> = new Subject<void>();

  private abortController: AbortController | null = null;
  private connecting = false;
  private sessionExpiredHandled = false;
  private tokenExpirationTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatWatchdogTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private manualDisconnect = false;
  private reconnectAttempts = 0;
  private wasConnected = false;
  private refreshTokenPromise: Promise<boolean> | null = null;
  private reconnectAfterAuthFailure = false;
  private readonly sessionExpiredListener = () => this.disconnect();
  private readonly pageCloseHandler = (event: Event) =>
    this.releasePresenceOnPageClose(event.type);
  private readonly browserRecoveryHandler = (event: Event) =>
    this.verifyConnectionAfterBrowserRecovery(event.type);

  constructor(
    private router: Router,
    private toastService: NotificationService,
    private authSessionService: AuthSessionService,
    private ngZone: NgZone,
  ) {
    window.addEventListener('chatlink-session-expired', this.sessionExpiredListener);
  }

  connect(): void {
    if (this.abortController || this.connecting) {
      return;
    }

    this.manualDisconnect = false;
    this.sessionExpiredHandled = false;
    this.connectionStatusState.set('connecting');
    this.connecting = true;
    this.abortController = new AbortController();
    this.bindPageCloseDisconnect();
    this.bindBrowserRecoveryMonitoring();
    const controller = this.abortController;

    this.authSessionService.getValidToken()
      .then((session) => {
        if (!session) {
          this.connectionStatusState.set('disconnected');
          controller.abort();
          return null;
        }

        return environment.sseStreamUrl;
      })
      .then((sseUrl) => {
        if (!sseUrl) {
          return;
        }
        if (!controller || controller.signal.aborted) {
          return;
        }

        if (!this.authSessionService.isAuthenticated()) {
          this.connectionStatusState.set('disconnected');
          return;
        }

        return fetchEventSource(sseUrl, {
          method: 'GET',
          credentials: 'include',
          headers: {
            Accept: 'text/event-stream',
          },
          signal: controller.signal,
          openWhenHidden: true,
          onopen: async (response) => {
            if (this.isAuthStatus(response.status)) {
              const refreshed = await this.authSessionService.refreshToken();
              if (refreshed) {
                this.reconnectAfterAuthFailure = true;
                this.closeCurrentConnection();
              } else {
                this.handleSessionExpired();
              }
              throw new Error(
                `[SSE] Autenticacion rechazada con status ${response.status}`
              );
            }

            if (!response.ok) {
              throw new Error(
                `[SSE] Conexion rechazada con status ${response.status}`
              );
            }

            this.connectionStatusState.set('connected');
          },
          onmessage: (ev) => {
            if (!ev.data) {
              return;
            }

            if (ev.event === 'sseConnection') {
              this.handleSseConnectionEvent(ev.data);
              return;
            }

            if (ev.event === 'heartbeat') {
              this.handleHeartbeatEvent(ev.data);
              return;
            }

            if (ev.event !== 'chatlink-event') {
              return;
            }

            try {
              const envelope = JSON.parse(ev.data) as SseEventEnvelope;
              this.routeUnifiedEvent(envelope);
            } catch (error) {
              console.error('[SSE] No se pudo parsear un evento:', error);
            }
          },
          onerror: (error) => {
            if (this.reconnectAfterAuthFailure) {
              throw error;
            }
            this.handleConnectionDrop('error', error);
            throw error;
          },
          onclose: () => {
            this.handleConnectionDrop('closed');
          },
        });
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          console.error('[SSE] Conexion finalizada por error:', error);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted && !this.manualDisconnect) {
          this.handleConnectionDrop('finished');
        }

        this.connecting = false;
        if (this.abortController === controller) {
          this.abortController = null;
        }
        if (this.reconnectAfterAuthFailure && !this.manualDisconnect) {
          this.reconnectAfterAuthFailure = false;
          this.clearReconnectTimer();
          this.connect();
        }
      });
  }

  disconnect(): void {
    this.manualDisconnect = true;
    this.connectionStatusState.set('disconnected');
    this.markSseDisconnected('manual');
    this.clearTokenExpirationTimer();
    this.clearHeartbeatWatchdog();
    this.clearReconnectTimer();
    this.unbindPageCloseDisconnect();
    this.unbindBrowserRecoveryMonitoring();

    if (!this.abortController) {
      this.connecting = false;
      return;
    }

    this.abortController.abort();
    this.abortController = null;
    this.connecting = false;
  }

  reconnectAuthenticatedSession(): void {
    this.manualDisconnect = false;
    this.sessionExpiredHandled = false;
    this.reconnectAttempts = 0;
    this.refreshTokenPromise = null;
    this.clearReconnectTimer();
    this.clearTokenExpirationTimer();
    this.clearHeartbeatWatchdog();
    this.bindBrowserRecoveryMonitoring();

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this.connecting = false;
    this.markSseDisconnected('login-reconnect');
    this.connect();
  }

  // beforeunload puede cancelarse (p. ej. "Salir del sitio"), por eso no se corta el stream aca:
  // el beacon libera la presencia y el backend cierra la conexion. Si la navegacion se cancela,
  // el front reconecta por el evento 'disconnecting' y el watchdog.
  private releasePresenceOnPageClose(reason: string): void {
    this.sendDisconnectBeacon(reason);
  }

  private closeCurrentConnection(): void {
    if (!this.abortController) {
      this.connecting = false;
      return;
    }

    this.abortController.abort();
    this.abortController = null;
    this.connecting = false;
  }

  private routeUnifiedEvent(envelope: SseEventEnvelope): void {
    const rawEventType = String(envelope?.eventType || '').trim();
    if (!rawEventType) {
      console.error('[SSE] Envelope sin eventType, se descarta:', envelope);
      return;
    }

    const eventType = this.normalizeUnifiedEventType(rawEventType);

    switch (eventType) {
      case 'NEW_MESSAGE':
        this.messageReceived.next(envelope.data as Message);
        return;
      case 'MY_MESSAGE':
        this.myMessageReceived.next(envelope.data as Message);
        return;
      case 'MESSAGE_STATUS':
        this.statusReceived.next(envelope.data as Status);
        return;
      case 'INTERNAL_MESSAGE':
        this.internalMessageReceived.next(envelope.data as InternalChatMessage);
        return;
      case 'INTERNAL_MESSAGE_STATUS':
        this.internalMessageStatusReceived.next(
          envelope.data as InternalMessageStatus
        );
        return;
      case 'INTERNAL_USER_PRESENCE':
        this.internalUserPresenceReceived.next(
          envelope.data as InternalUserPresence
        );
        return;
      case 'INTERNAL_USERS_PRESENCE_SNAPSHOT':
        this.internalUsersPresenceSnapshotReceived.next(
          envelope.data as InternalUserPresence[]
        );
        return;
      case 'EXTERNAL_CHAT_VIEWERS':
        this.externalChatViewersReceived.next(
          envelope.data as ExternalChatViewers
        );
        return;
      case 'CONVERSATION_STATE_CHANGED':
        this.conversationStateChanged.next(
          envelope.data as ConversationStateSseEvent
        );
        return;
      case 'SENDER_UNREAD_CHANGED':
        this.senderUnreadChanged.next(envelope.data as SenderUnreadSseEvent);
        return;
      case 'CONVERSATION_DERIVACION':
        this.conversationDerivacionReceived.next(
          envelope.data as ConversationDerivacionSseEvent
        );
        return;
      case 'CONVERSATION_DERIVACION_POPUP':
        this.conversationDerivacionPopupReceived.next({
          ...(envelope.data as ConversationDerivacionPopupSseEvent),
          sseEnvelopeId: envelope.id,
          sseEventType: eventType,
        });
        return;
      case 'SIDEBAR_CHANGED':
        this.sidebarChanged.next(envelope.data as SidebarSseEvent);
        return;
      default:
        console.warn(`[SSE] eventType no soportado: ${eventType}`, envelope);
        return;
    }
  }

  private normalizeUnifiedEventType(rawEventType: string): string {
    const normalized = rawEventType
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '_');

    switch (normalized) {
      case 'CONVERSATION_DERIVATION':
        return 'CONVERSATION_DERIVACION';
      case 'CONVERSATION_DERIVATION_POPUP':
        return 'CONVERSATION_DERIVACION_POPUP';
      case 'SENDER_UNREAD_CHANGE':
        return 'SENDER_UNREAD_CHANGED';
      case 'SIDEBAR_CHANGE':
        return 'SIDEBAR_CHANGED';
      default:
        return normalized;
    }
  }

  private buildDisconnectBeaconUrl(): string {
    const params = new URLSearchParams();

    const connectionId = this.sseHealthState().connectionId;
    if (connectionId) {
      params.set('connectionId', connectionId);
    }

    return `${environment.apiUrl}/internal-chat/presence/disconnect-beacon?${params.toString()}`;
  }

  private sendDisconnectBeacon(reason: string): void {
    if (!this.authSessionService.isAuthenticated()) {
      return;
    }

    const url = this.buildDisconnectBeaconUrl();
    const csrfToken = this.readCookie('XSRF-TOKEN');
    if (!csrfToken) return;

    void fetch(url, {
      method: 'POST',
      keepalive: true,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': csrfToken,
      },
      body: '{}',
    }).catch(() => undefined);
  }

  private readCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const prefix = `${name}=`;
    const cookie = document.cookie.split(';').map(value => value.trim()).find(value => value.startsWith(prefix));
    return cookie ? decodeURIComponent(cookie.substring(prefix.length)) : null;
  }

  private handleSseConnectionEvent(rawData: string): void {
    try {
      const data = JSON.parse(rawData) as {
        status?: 'connected' | 'disconnecting';
        connectionId?: string;
        heartbeatIntervalMs?: number;
        reconnectTimeMs?: number;
        jwtExpiresAt?: string;
        reason?: string;
        timestamp?: string;
      };

      if (data.status === 'connected') {
        const wasConnected = this.wasConnected;
        this.wasConnected = true;
        this.reconnectAttempts = 0;
        this.sseHealthState.set({
          healthy: true,
          connectionId: data.connectionId || null,
          lastHeartbeatAt: Date.now(),
          heartbeatIntervalMs:
            data.heartbeatIntervalMs || this.defaultHeartbeatIntervalMs,
          reconnectTimeMs: data.reconnectTimeMs || this.reconnectDelayMs,
          jwtExpiresAt: data.jwtExpiresAt || null,
          reason: null,
        });
        this.connectionStatusState.set('connected');
        this.startHeartbeatWatchdog();
        this.scheduleTokenExpirationDisconnectFromConnection(data.jwtExpiresAt);

        if (wasConnected) {
          this.reconnected.next();
        }
        return;
      }

      if (data.status === 'disconnecting') {
        this.markSseDisconnected(data.reason || 'disconnecting');
        this.closeCurrentConnection();

        if (data.reason === 'jwt-expired') {
          this.handleJwtExpiredDisconnect();
          return;
        }

        this.scheduleReconnect(data.reconnectTimeMs);
      }
    } catch (error) {
      console.error('[SSE] No se pudo parsear sseConnection:', error);
    }
  }

  private handleHeartbeatEvent(rawData: string): void {
    try {
      const data = JSON.parse(rawData) as {
        connectionId?: string;
        timestamp?: string;
      };
      const currentHealth = this.sseHealthState();

      if (
        currentHealth.connectionId &&
        data.connectionId &&
        currentHealth.connectionId !== data.connectionId
      ) {
        return;
      }

      this.sseHealthState.set({
        ...currentHealth,
        healthy: true,
        lastHeartbeatAt: Date.now(),
        reason: null,
      });

      const connectionId = data.connectionId ?? currentHealth.connectionId;
      if (connectionId) {
        this.sendHeartbeatAck(connectionId);
      }
    } catch (error) {
      console.error('[SSE] No se pudo parsear heartbeat:', error);
    }
  }

  // Confirma al backend que este cliente sigue recibiendo heartbeats; sin ack el servidor da la conexion por obsoleta.
  private sendHeartbeatAck(connectionId: string): void {
    const csrfToken = this.readCookie('XSRF-TOKEN');
    if (!csrfToken) return;

    const params = new URLSearchParams({ connectionId });
    void fetch(`${environment.apiUrl}/sse/heartbeat-ack?${params.toString()}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-XSRF-TOKEN': csrfToken },
    }).catch(() => undefined);
  }

  private startHeartbeatWatchdog(): void {
    this.clearHeartbeatWatchdog();
    this.ngZone.runOutsideAngular(() => {
      this.heartbeatWatchdogTimer = window.setInterval(() => {
        if (document.hidden || !document.hasFocus()) {
          return;
        }

        const health = this.sseHealthState();
        if (!health.healthy || !health.lastHeartbeatAt) {
          return;
        }

        const timeoutMs =
          (health.heartbeatIntervalMs || this.defaultHeartbeatIntervalMs) *
          this.heartbeatTimeoutFactor;

        if (Date.now() - health.lastHeartbeatAt > timeoutMs) {
          this.ngZone.run(() => this.handleConnectionDrop('heartbeat-timeout'));
        }
      }, 5000);
    });
  }

  private clearHeartbeatWatchdog(): void {
    if (!this.heartbeatWatchdogTimer) {
      return;
    }

    clearInterval(this.heartbeatWatchdogTimer);
    this.heartbeatWatchdogTimer = null;
  }

  private handleConnectionDrop(reason: string, error?: unknown): void {
    if (this.manualDisconnect) {
      return;
    }

    if (error) {
      console.error('[SSE] Conexion perdida:', error);
    }

    this.markSseDisconnected(reason);
    this.closeCurrentConnection();

    if (this.isCurrentTokenExpired()) {
      this.refreshTokenAndReconnect();
      return;
    }

    this.scheduleReconnect();
  }

  private scheduleReconnect(delayOverrideMs?: number): void {
    if (this.manualDisconnect || this.reconnectTimer) {
      return;
    }

    if (!this.authSessionService.isAuthenticated()) {
      this.connectionStatusState.set('disconnected');
      return;
    }

    this.reconnectAttempts += 1;
    const health = this.sseHealthState();
    const baseDelay =
      delayOverrideMs || health.reconnectTimeMs || this.reconnectDelayMs;
    const linearDelay = Math.min(
      baseDelay * Math.max(1, this.reconnectAttempts),
      this.maxReconnectDelayMs,
    );
    // Jitter de ±20%: evita que todos los clientes reconecten en el mismo instante tras un reinicio del servidor.
    const backoffDelay = Math.round(linearDelay * (0.8 + Math.random() * 0.4));

    this.connectionStatusState.set('connecting');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.authSessionService.getValidToken().then((session) => {
        if (!session || this.manualDisconnect) {
          this.connectionStatusState.set('disconnected');
          return;
        }

        this.connect();
      });
    }, backoffDelay);
  }

  private clearReconnectTimer(): void {
    if (!this.reconnectTimer) {
      return;
    }

    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private markSseDisconnected(reason: string): void {
    const health = this.sseHealthState();
    this.sseHealthState.set({
      ...health,
      healthy: false,
      lastHeartbeatAt: null,
      reason,
    });
    this.connectionStatusState.set('disconnected');
    this.clearHeartbeatWatchdog();
  }

  private scheduleTokenExpirationDisconnectFromConnection(jwtExpiresAt?: string): void {
    if (!jwtExpiresAt) {
      return;
    }

    const expirationMs = Date.parse(jwtExpiresAt);
    if (!Number.isFinite(expirationMs)) {
      return;
    }

    this.clearTokenExpirationTimer();
    const delayMs = expirationMs - Date.now();
    if (delayMs <= 0) {
      this.handleJwtExpiredDisconnect();
      return;
    }

    this.tokenExpirationTimer = setTimeout(() => {
      this.handleJwtExpiredDisconnect();
    }, delayMs);
  }

  private clearTokenExpirationTimer(): void {
    if (!this.tokenExpirationTimer) {
      return;
    }

    clearTimeout(this.tokenExpirationTimer);
    this.tokenExpirationTimer = null;
  }

  private isCurrentTokenExpired(): boolean {
    return this.authSessionService.isAccessTokenExpired(5_000);
  }

  private handleJwtExpiredDisconnect(): void {
    if (this.manualDisconnect) {
      return;
    }

    this.markSseDisconnected('jwt-expired');
    this.closeCurrentConnection();

    if (!this.isCurrentTokenExpired()) {
      this.clearReconnectTimer();
      this.connect();
      return;
    }

    this.refreshTokenAndReconnect();
  }

  private refreshTokenAndReconnect(): void {
    if (!this.refreshTokenPromise) {
      this.refreshTokenPromise = this.refreshToken().finally(() => {
        this.refreshTokenPromise = null;
      });
    }

    this.refreshTokenPromise.then((refreshed) => {
      if (!refreshed || this.manualDisconnect) {
        return;
      }

      this.sessionExpiredHandled = false;
      this.clearReconnectTimer();
      this.connect();
    });
  }

  private async refreshToken(): Promise<boolean> {
    return this.authSessionService.refreshToken();
  }

  private bindPageCloseDisconnect(): void {
    window.removeEventListener('beforeunload', this.pageCloseHandler);
    window.addEventListener('beforeunload', this.pageCloseHandler);
  }

  private unbindPageCloseDisconnect(): void {
    window.removeEventListener('beforeunload', this.pageCloseHandler);
  }

  private bindBrowserRecoveryMonitoring(): void {
    window.removeEventListener('online', this.browserRecoveryHandler);
    window.removeEventListener('focus', this.browserRecoveryHandler);
    document.removeEventListener('visibilitychange', this.browserRecoveryHandler);

    window.addEventListener('online', this.browserRecoveryHandler);
    window.addEventListener('focus', this.browserRecoveryHandler);
    document.addEventListener('visibilitychange', this.browserRecoveryHandler);
  }

  private unbindBrowserRecoveryMonitoring(): void {
    window.removeEventListener('online', this.browserRecoveryHandler);
    window.removeEventListener('focus', this.browserRecoveryHandler);
    document.removeEventListener('visibilitychange', this.browserRecoveryHandler);
  }

  private verifyConnectionAfterBrowserRecovery(reason: string): void {
    if (reason === 'visibilitychange' && document.hidden) {
      return;
    }

    if (this.manualDisconnect || !this.authSessionService.isAuthenticated()) {
      return;
    }

    if (!this.abortController && !this.connecting) {
      this.connect();
      return;
    }

    const health = this.sseHealthState();
    if (!health.lastHeartbeatAt) {
      return;
    }

    const timeoutMs =
      (health.heartbeatIntervalMs || this.defaultHeartbeatIntervalMs) *
      this.heartbeatTimeoutFactor;

    if (Date.now() - health.lastHeartbeatAt > timeoutMs) {
      if (reason === 'online') {
        this.handleConnectionDrop(`${reason}-stale`);
        return;
      }

      // Background tabs may defer heartbeat callbacks even when the stream stays open.
      // Give the active stream one fresh heartbeat window before declaring it lost.
      this.sseHealthState.set({
        ...health,
        lastHeartbeatAt: Date.now(),
        reason: null,
      });
    }
  }

  private isAuthStatus(status: number): boolean {
    return status === 401 || status === 403;
  }

  private handleSessionExpired(): void {
    if (this.sessionExpiredHandled) return;
    this.sessionExpiredHandled = true;
    this.authSessionService.expireSession();
    this.disconnect();
  }
}
